import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ContainerItemModel,
  ContainerItemOption,
  ContainerModel,
} from '@app/models/server/layout.model';
import { OlaMaps } from '@app/ola-maps';
import { DetailPageOlaMapServices } from '@app/new-ui-lib/b2b-maps/detail-page-ola-map/detail-page-ola-map.services';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpClientService } from '@app/services/util-services/root-services/http-client.service';
import { ToastService } from '@app/services/util-services/root-services/toast-service';
import { environment } from '@src/environments/environment';
import { B2bPopoverV1Component } from '@app/new-ui-lib/b2b-popover-v1/b2b-popover-v1.component';
import { IonList } from '@ionic/angular/standalone';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'app-detail-page-ola-map',
  templateUrl: './detail-page-ola-map.component.html',
  styleUrls: ['./detail-page-ola-map.component.scss'],
  standalone: true,
  providers: [DetailPageOlaMapServices],
  imports: [ReactiveFormsModule, B2bPopoverV1Component, IonList, TitleCasePipe],
})
export class DetailPageOlaMapComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input({ required: true }) containerName: 'd-bdp-omap' | 'm-bdp-omap';
  @Input({ required: true }) center: { lat: number; lng: number };
  @Input() zoom = 12;
  @Input() mapConfig: {
    mainProperty: ContainerModel;
    otherProperties: ContainerModel[];
    overrideData: boolean;
  };
  isMobile: boolean =
    environment?.mobileApp || !this.b2bDeviceUtilService.isDesktop();
  olaMaps: OlaMaps;
  private myMap;

  mapLoaded = false;

  public searchControl: FormControl = new FormControl();
  query: string;

  protected readonly environment = environment;

  displayOptions: ContainerItemOption[] = [];
  selectedOptions: ContainerItemOption[] = [];

  optionsSubscription$: Subscription;
  getListSubscription$: Subscription;
  getPlaceDetailSubscription$: Subscription;

  constructor(
    public detailPageOlaMapServices: DetailPageOlaMapServices,
    private httpClientService: HttpClientService,
    private toastService: ToastService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
  ) {}

  ngOnInit(): void {
    this.optionsSubscription$ = this.searchControl.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(400))
      .subscribe({
        next: (val: string) => {
          this.query = val;
          if (
            this.query !== undefined &&
            this.query !== null &&
            this.query?.trim() !== ''
          ) {
            this.getList(val);
          }
        },
      });
  }

  ngAfterViewInit(): void {
    if (!this.b2bDeviceUtilService.isServer()) {
      this.setupMap();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {}

  setupMap() {
    this.olaMaps = this.detailPageOlaMapServices.initOlaMaps();
    this.myMap = this.detailPageOlaMapServices.createDetailPageMap(
      this.containerName,
      this.center,
      this.zoom,
    );

    if (this.myMap) {
      this.detailPageOlaMapServices.setPropertyMarkers([
        this.mapConfig?.mainProperty,
      ]);
      this.mapLoaded = true;
    }
  }

  private getList(searchQuery: string): void {
    // API - /google-services/ola-maps/autocomplete
    // post api
    const body: {
      searchTag: string;
      location: {
        lng: number;
        lat: number;
      };
    } = {
      searchTag: searchQuery,
      location: this.center,
    };

    // const apiURL: string = this.createApiUrl(searchQuery, page, size);
    this.getListSubscription$ = this.httpClientService
      .post('google-services/autocomplete', body, {})
      .subscribe({
        next: (result: ContainerItemModel[]) => {
          if (result?.length === 1) {
            if (result[0]?.options?.length > 0) {
              this.displayOptions = result[0]?.options;
            } else {
              this.displayOptions = [];
              this.toastService.showApiError('Try a different search.');
            }
          } else {
            this.displayOptions = [];
            this.toastService.showApiError('Try a different search.');
          }
        },
        error: (error: HttpErrorResponse) => {
          // console.error(error);
          this.toastService.showApiError(error);
        },
      });
  }

  getPlaceDetailByPlaceId(option: ContainerItemOption) {
    const body: {
      placeId: string;
      from: {
        lng: number;
        lat: number;
      };
      to: {
        lng: number;
        lat: number;
      };
    } = {
      placeId: option?._id as string,
      to: {
        lng: option?.location?.coordinates?.[0],
        lat: option?.location?.coordinates?.[1],
      },
      from: this.center,
    };

    // const apiURL: string = this.createApiUrl(searchQuery, page, size);
    this.getPlaceDetailSubscription$ = this.httpClientService
      .post('google-services/place-distance-and-routing', body, {})
      .subscribe({
        next: (result: {
          totalDistance: string;
          placeDetail: ContainerItemOption;
          geometry: {
            type: string;
            coordinates: number[][];
          };
        }) => {
          if (result?.totalDistance) {
            if (!this.selectedOptions) {
              this.selectedOptions = [];
            }
            this.selectedOptions.push(result?.placeDetail);
            this.detailPageOlaMapServices.setLandmarkMarkerAndRoute(
              result?.totalDistance,
              result?.placeDetail ? result?.placeDetail : option,
              this.center,
              result?.geometry,
            );
          }
        },
        error: (error: HttpErrorResponse) => {
          // console.error(error);
          this.toastService.showApiError(error);
        },
      });
  }

  onItemClickFunc(option: ContainerItemOption, index: number): void {
    this.getPlaceDetailByPlaceId(option);
  }

  ngOnDestroy(): void {
    this.detailPageOlaMapServices?.destroy();

    if (this.optionsSubscription$) {
      this.optionsSubscription$?.unsubscribe();
    }
    if (this.getListSubscription$) {
      this.getListSubscription$.unsubscribe();
    }
    if (this.getPlaceDetailSubscription$) {
      this.getPlaceDetailSubscription$.unsubscribe();
    }
  }
}
